/**
 * ClientProfileContent — Contingut autònom del perfil de client.
 *
 * Component reutilitzable amb Tabs (Detalls | Missatges).
 * Consumit per:
 *   - ClientDetailSheet (lateral drawer)
 *   - ClientDetailPage (/clients/:id)
 */

import { useQuery } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import type { ClientLegacyStaysResponse, LegacyStayDto } from '@guilleumes/types';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Badge } from '@/components/ui/badge';
import { Separator } from '@/components/ui/separator';
import {
  User,
  Mail,
  Phone,
  MapPin,
  FileText,
  Star,
  Calendar,
  CreditCard,
  Building2,
  History,
  CalendarDays,
} from 'lucide-react';
import { ClientMessagesTab } from '@/components/messaging/ClientMessagesTab';
import { documentTypeLabel } from '@/lib/document-types';
import { formatCalendarDate } from '@/lib/dates';
import { useAuth } from '@/contexts/AuthContext';
import { ClientsService, type Client } from '@/services/clients';

export interface ClientProfileContentProps {
  /** Client data to display */
  client: Client;
  /** Default active tab */
  defaultTab?: 'detalls' | 'missatges';
}

function formatCurrency(amount: number): string {
  return new Intl.NumberFormat('ca-ES', {
    style: 'currency',
    currency: 'EUR',
  }).format(amount);
}

function InfoRow({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  value: string | null | undefined;
}) {
  if (!value) return null;
  return (
    <div className="flex items-center gap-3 py-2">
      <Icon className="h-4 w-4 text-muted-foreground shrink-0" />
      <div className="min-w-0">
        <p className="text-xs text-muted-foreground">{label}</p>
        <p className="text-sm font-medium truncate">{value}</p>
      </div>
    </div>
  );
}

/** «1 estada antiga» / «114 estades antigues». */
function legacyStaysLabel(count: number): string {
  return count === 1 ? '1 estada antiga' : `${count} estades antigues`;
}

/**
 * Estades del PMS antic (2010-2021), sota les reserves de Beds24.
 *
 * Només lectura i prou: `legacy_stays` és un arxiu (`plans/legacy-pms-import.md` §L4), aquí
 * no es crea ni s'edita res. Els apartaments no tenen `Unit` al PMS nou (R7), així que hi
 * surt el nom d'habitació de text; «no sincronitzada» marca la reserva del hostal de 2022+
 * que no va arribar mai a Beds24.
 */
function LegacyStaysSection({
  stays,
  lang,
  isAdmin,
}: {
  stays: LegacyStayDto[];
  lang: string;
  /** El calendari històric és només d'ADMIN (D7): a la resta no se'ls hi ofereix l'enllaç. */
  isAdmin: boolean;
}) {
  return (
    <section className="space-y-1">
      <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
        Estades del PMS antic ({stays.length})
      </h3>
      <ul className="mt-2 max-h-80 divide-y overflow-y-auto rounded-lg border">
        {stays.map((stay) => (
          <li key={stay.id} className="flex flex-wrap items-baseline gap-x-3 gap-y-1 px-3 py-2">
            <span className="text-sm font-medium tabular-nums">
              {formatCalendarDate(stay.checkIn, lang, 'd MMM yyyy')}
            </span>
            {stay.nights != null && (
              <span className="text-xs text-muted-foreground">
                {stay.nights === 1 ? '1 nit' : `${stay.nights} nits`}
              </span>
            )}
            <span className="text-xs text-muted-foreground">
              {stay.unitName ? `Hab. ${stay.unitName}` : stay.roomName}
            </span>
            {stay.channelName && (
              <span className="text-xs text-muted-foreground">{stay.channelName}</span>
            )}
            {stay.unsynced && (
              <Badge variant="outline" className="text-[10px]">
                no sincronitzada
              </Badge>
            )}
            <span className="ml-auto flex items-center gap-3">
              {stay.price != null && (
                <span className="text-sm tabular-nums">{formatCurrency(stay.price)}</span>
              )}
              {/* Obre la quinzena d'aquell check-in amb l'estada ressaltada (H2 de la spec). */}
              {isAdmin && (
                <Link
                  to={`/calendari/historic?date=${stay.checkIn.slice(0, 10)}&stay=${stay.id}`}
                  className="flex items-center gap-1 text-xs text-muted-foreground hover:text-foreground hover:underline"
                  title="Veure al calendari històric"
                >
                  <CalendarDays className="h-3.5 w-3.5 shrink-0" />
                  <span className="hidden sm:inline">calendari</span>
                </Link>
              )}
            </span>
          </li>
        ))}
      </ul>
    </section>
  );
}

function DetailsTab({
  client,
  legacy,
  lang,
  isAdmin,
}: {
  client: Client;
  legacy?: ClientLegacyStaysResponse;
  lang: string;
  isAdmin: boolean;
}) {
  const address = [client.adreca, client.ciutat, client.provincia, client.codi_postal, client.pais]
    .filter(Boolean)
    .join(', ');

  return (
    <div className="space-y-6">
      {/* Identity */}
      <section className="space-y-1">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Identitat
        </h3>
        <div className="space-y-0">
          <InfoRow icon={User} label="Nom complet" value={client.nom_complet} />
          <InfoRow icon={Mail} label="Email" value={client.email} />
          <InfoRow icon={Phone} label="Telèfon" value={client.telefon} />
          {client.mobil && <InfoRow icon={Phone} label="Mòbil" value={client.mobil} />}
          <InfoRow
            icon={FileText}
            label={documentTypeLabel(client.tipus_document)}
            value={client.num_document}
          />
        </div>
      </section>

      <Separator />

      {/* Company */}
      {(client.empresa || client.nif_empresa) && (
        <>
          <section className="space-y-1">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Empresa
            </h3>
            <InfoRow icon={Building2} label="Empresa" value={client.empresa} />
            <InfoRow icon={FileText} label="NIF Empresa" value={client.nif_empresa} />
          </section>
          <Separator />
        </>
      )}

      {/* Address */}
      {address && (
        <>
          <section className="space-y-1">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Adreça
            </h3>
            <InfoRow icon={MapPin} label="Adreça completa" value={address} />
          </section>
          <Separator />
        </>
      )}

      {/* Stats */}
      <section className="space-y-1">
        <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
          Estadístiques
        </h3>
        <div className="grid grid-cols-2 gap-3 mt-2">
          <div className="p-3 bg-muted/50 rounded-lg text-center">
            <Calendar className="h-5 w-5 mx-auto text-blue-500 mb-1" />
            <p className="text-lg font-bold">{client.total_reserves}</p>
            <p className="text-xs text-muted-foreground">Reserves</p>
          </div>
          <div className="p-3 bg-muted/50 rounded-lg text-center">
            <CreditCard className="h-5 w-5 mx-auto text-green-500 mb-1" />
            <p className="text-lg font-bold">{formatCurrency(client.total_gastat)}</p>
            <p className="text-xs text-muted-foreground">Total gastat</p>
          </div>
        </div>
        {client.primera_reserva && (
          <p className="text-xs text-muted-foreground mt-2">
            Primera reserva: {new Date(client.primera_reserva).toLocaleDateString('ca-ES')}
          </p>
        )}
        {client.ultima_reserva && (
          <p className="text-xs text-muted-foreground">
            Última reserva: {new Date(client.ultima_reserva).toLocaleDateString('ca-ES')}
          </p>
        )}
      </section>

      {/* Estades del PMS antic: res si el client no en té (L4) */}
      {legacy && legacy.stays.length > 0 && (
        <>
          <Separator />
          <LegacyStaysSection stays={legacy.stays} lang={lang} isAdmin={isAdmin} />
        </>
      )}

      {/* Notes (Person.notes, o Client.notes si no hi ha persona) */}
      {client.notes && (
        <>
          <Separator />
          <section className="space-y-1">
            <h3 className="text-sm font-medium text-muted-foreground uppercase tracking-wide">
              Notes
            </h3>
            <div className="p-3 bg-yellow-50 border border-yellow-200 rounded-lg text-sm">
              <p className="text-xs font-medium text-yellow-700 mb-1">Notes internes</p>
              <p className="text-yellow-900 whitespace-pre-wrap">{client.notes}</p>
            </div>
          </section>
        </>
      )}
    </div>
  );
}

export function ClientProfileContent({
  client,
  defaultTab = 'detalls',
}: ClientProfileContentProps) {
  const { i18n } = useTranslation();
  const lang = i18n.resolvedLanguage || i18n.language;
  const { user } = useAuth();
  const isAdmin = user?.role === 'ADMIN';

  /**
   * Estades del PMS antic. Fora de `GET /clients/:id` a posta (la llista no té sostre:
   * una empresa en pot tenir més de cent), amb la seva clau i un `staleTime` llarg:
   * `legacy_stays` és un arxiu tancat, no canvia mentre la fitxa és oberta.
   *
   * Sense toast si falla: és un bloc informatiu, i un error deixa la fitxa igual que un
   * client sense estades antigues (no es pinta res).
   */
  const { data: legacy } = useQuery({
    queryKey: ['clients', client.id, 'legacy-stays'],
    queryFn: () => ClientsService.getLegacyStays(client.id),
    staleTime: 10 * 60 * 1000,
  });

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center gap-3">
        <div className="flex h-12 w-12 items-center justify-center rounded-full bg-gradient-to-br from-green-500 to-emerald-600 text-white font-bold text-lg">
          {client.nom?.[0]?.toUpperCase() || '?'}
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-2">
            <h2 className="text-lg font-semibold truncate">{client.nom_complet}</h2>
            {(client.es_vip || client.total_reserves >= 5 || client.total_gastat >= 2000) && (
              <Star className="h-4 w-4 text-yellow-500 shrink-0" />
            )}
          </div>
          <div className="flex items-center gap-2">
            <Badge variant={client.actiu ? 'default' : 'secondary'}>
              {client.actiu ? 'Actiu' : 'Inactiu'}
            </Badge>
            {client.tipus_client && <Badge variant="outline">{client.tipus_client}</Badge>}
            {/* Senyals de la card (§6 F2.6): es canvia a «Editar» */}
            {client.payOnArrival && (
              <Badge
                variant="outline"
                className="border-amber-300 text-amber-800"
                title="Client de confiança: les seves reserves es cobren en marxar"
              >
                ★ Paga en arribar
              </Badge>
            )}
          </div>
          {/* «Client des de …» només si té estades al PMS antic (L4) */}
          {legacy && legacy.legacyStays > 0 && legacy.legacySince != null && (
            <p className="mt-1 flex items-center gap-1.5 text-xs text-muted-foreground">
              <History className="h-3.5 w-3.5 shrink-0" />
              Client des de {legacy.legacySince} · {legacyStaysLabel(legacy.legacyStays)}
            </p>
          )}
        </div>
      </div>

      <Separator />

      {/* Tabs */}
      <Tabs defaultValue={defaultTab} className="w-full">
        <TabsList className="w-full">
          <TabsTrigger value="detalls" className="flex-1">
            Detalls
          </TabsTrigger>
          <TabsTrigger value="missatges" className="flex-1">
            Missatges
          </TabsTrigger>
        </TabsList>

        <TabsContent value="detalls" className="mt-4">
          <DetailsTab client={client} legacy={legacy} lang={lang} isAdmin={isAdmin} />
        </TabsContent>

        <TabsContent value="missatges" className="mt-4">
          <ClientMessagesTab clientId={client.id} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
